import { Alert, Button, Form, Select } from "antd";
import { DeleteOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import { useState } from "react";

import { SalesProcessSelect } from "components/selects/sales-process-select";
import { useOrganization } from "data/use-organization";
import { useSalesProcesses } from "data/use-sales-processes";
import { usePatchOrganizationSettings } from "../../../data/use-organization";
import { useOnChange } from "hooks/use-on-change";
import { Modal } from "components/modal";

const onSubmit =
  ({ organization, migration, patchOrganizationSettings, setError }) =>
  (values) => {
    let opportunitySalesProcessMigrations =
      organization.settings?.opportunitySalesProcessMigrations ?? [];

    if (migration) {
      const index = opportunitySalesProcessMigrations.findIndex(
        ({ id }) => id === migration.id
      );

      opportunitySalesProcessMigrations.splice(index, 1, {
        ...values,
        id: migration.id,
      });
    } else {
      opportunitySalesProcessMigrations.push({
        id: crypto.randomUUID(),
        ...values,
      });
    }

    setError();

    patchOrganizationSettings.mutate({
      opportunitySalesProcessMigrations,
    });
  };

export const MigrationModal = (props) => {
  const { close, isOpen, migration } = props;

  const [error, setError] = useState();

  const organization = useOrganization();
  const salesProcesses = useSalesProcesses();

  const patchOrganizationSettings = usePatchOrganizationSettings({
    onSuccess: async (_result, { mutation }) => {
      await mutation.onSuccess.call();

      close();
    },
  });

  const [form] = Form.useForm();

  const fromId = Form.useWatch("fromId", form);
  const toId = Form.useWatch("toId", form);

  useOnChange(
    isOpen,
    () => {
      if (!isOpen) {
        return;
      }

      if (migration) {
        form.setFieldsValue({
          ...migration,
        });
      } else {
        form.setFieldsValue({
          fromId: "",
          toId: "",
          steps: [],
        });
      }
    },
    [isOpen, form, migration]
  );

  const getOptions = (id) => {
    const salesProcess = salesProcesses
      .all()
      .find((s) => Number(s._id) === Number(id));

    if (!salesProcess) {
      return [];
    }

    return salesProcess.steps.map(({ _id, question }) => ({
      value: _id,
      label: question,
    }));
  };

  return (
    <Modal
      confirmLoading={patchOrganizationSettings.isLoading}
      maskClosable={false}
      okText={"Create"}
      onCancel={close}
      onOk={form.submit}
      open={isOpen}
      title={"Sales Process Migration"}
      width={"756px"}
    >
      <div>
        When an opportunity updates it's sales process from the{" "}
        <strong>From Process</strong> to the <strong>To Process</strong>, any
        completed steps in the left column will be completed as the steps in the
        to right column.
      </div>
      <div style={{ margin: "18px 0px" }}>
        This will be done with step overrides.
      </div>
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        onFinish={onSubmit({
          migration,
          organization,
          patchOrganizationSettings,
          setError,
        })}
      >
        <div id="create-sales-process-modal">
          <SalesProcessSelect
            onChange={(value) => {
              if (value !== fromId) {
                form.setFieldValue("steps", []);
              }
            }}
            name="fromId"
            label="From Process"
            rules={[{ required: true }]}
          />
          <SalesProcessSelect
            onChange={(value) => {
              if (value !== toId) {
                form.setFieldValue("steps", []);
              }
            }}
            name="toId"
            label="To Process"
            rules={[{ required: true }]}
          />
        </div>
        <Form.List name="steps">
          {(fields, arrayHelpers) => {
            return (
              <div style={{ margin: "24px 0px 12px 0px" }}>
                <div
                  style={{
                    alignItems: "flex-end",
                    display: "flex",
                    marginBottom: "24px",
                  }}
                >
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => {
                      arrayHelpers.add({
                        fromStepId: "",
                        toStepId: "",
                      });
                    }}
                    style={{ marginLeft: "auto" }}
                  >
                    Add Step
                  </Button>
                </div>
                {fields.map(({ key, name }) => {
                  return (
                    <div
                      key={key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        height: "58px",
                      }}
                    >
                      <Form.Item
                        labelCol={{ span: 0 }}
                        wrapperCol={{ span: 24 }}
                        name={[name, "fromStepId"]}
                        rules={[{ required: true }]}
                        style={{ margin: "0px 12px", width: "50%" }}
                      >
                        <Select options={getOptions(fromId)} />
                      </Form.Item>
                      <RightOutlined />
                      <Form.Item
                        labelCol={{ span: 0 }}
                        wrapperCol={{ span: 24 }}
                        name={[name, "toStepId"]}
                        rules={[{ required: true }]}
                        style={{ margin: "0px 12px", flexGrow: 1 }}
                      >
                        <Select options={getOptions(toId)} />
                      </Form.Item>
                      <Button
                        icon={<DeleteOutlined />}
                        onClick={() => arrayHelpers.remove(index)}
                      />
                    </div>
                  );
                })}
              </div>
            );
          }}
        </Form.List>
      </Form>

      {error && (
        <Alert
          message={error}
          style={{ margin: "-24px 0px 12px 0px" }}
          type="error"
        />
      )}
    </Modal>
  );
};
